// Content for the high-intent platform landing pages (/ai-chatbot-for-<platform>).
//
// Rules for anything added here:
//   1. Only claim what PaperBrainz actually does. The install really is one <script> tag.
//   2. Do NOT invent platform UI click-paths in fine detail — Wix/WordPress/Shopify rename
//      menu items regularly, and a wrong instruction is worse than a general one. Describe
//      where the tag goes and let the platform's own docs cover the exact clicks.
//   3. Limitations are stated, not hidden. A page that admits a constraint converts better
//      than one that gets found out after signup.
//   4. No competitor names, prices or feature claims — we cannot keep those accurate.

// The four features the Wix App Market listing leads with, so the site and the
// marketplace tell the same story. Listing wording kept where it is accurate.
//
// ONE DELIBERATE DIVERGENCE: the listing says "28 Languages, Auto-Detected — Serve
// global visitors instantly with automatic language detection". That is not what the
// product does. Verified Aug 2026: widget.js sends only {client_id, message,
// chat_history} to the chat service with no language field, chat_history starts empty,
// and a live test replied to "hola" in English. The 28 is the widget-text count in
// backend/locales/languageDefaults.js, chosen by the site owner in settings.
// Do not copy the auto-detect claim across from the listing — fix the listing instead.
export const PRODUCT_HIGHLIGHTS = [
    {
        icon: "upload",
        title: "Trained on your content",
        desc: "Upload PDFs, DOC and DOCX files, paste text, add Q&A pairs, or import the pages of your live site. The chatbot answers from those sources and nothing else.",
    },
    {
        icon: "palette",
        title: "Fully customisable widget",
        desc: "Match your brand with custom colours, 40 Google Fonts, your own logo, light or dark theme, three sizes, four positions and seven chat button icons.",
    },
    {
        icon: "globe",
        title: "Widget text in 28 languages",
        desc: "The greeting, placeholder and suggested questions ship ready in 28 languages. You choose yours in settings, and the chatbot can hold conversations in other languages too.",
    },
    {
        icon: "chart",
        title: "Built-in analytics and usage tracking",
        desc: "Monitor chat volume, read back what customers actually asked, and see how much of your monthly allowance you have used — without adding another analytics tool.",
    },
];

// Showcase slides.
//
// APP_MARKET_SLIDES are the six carousel cards from the live Wix App Market listing, in
// the same order they appear there (re-downloaded Aug 2026 after the listing artwork was
// replaced; note the FIRST image URL in that markup is the app icon, not a slide).
// Wix-ONLY: there is no PaperBrainz app in the Shopify or WordPress marketplaces, so
// "Add to Site / no code / one click" would be false there. A Shopify app is planned;
// WordPress is not. When Shopify ships, give it its own slides rather than reusing these.
//
// Other platforms show the same kind of walkthrough built from the in-app product
// mockups instead — same presentation, claims that are true for them.
//
// Stored as WebP at 1600px wide: the previous five PNGs were 2.2 MB between them, which
// is most of a platform page's weight for images the visitor may never scroll to.
//
// NOTE ON CLAIMS: this artwork replaced a set that claimed source citations, 30+
// auto-detected languages and human handoff, none of which exist. TWO CARDS ARE STILL
// AWAITING RE-EXPORT BY THE OWNER (agreed Aug 2026) — do not treat them as verified:
//   - wix-answers says "It never guesses" and "when it genuinely doesn't know, it hands
//     the customer to your support email". support_email renders in exactly one place —
//     the disabled/rate-limited notice in widget-ui.ts chatHtml() — never on a
//     low-confidence answer, and there is no escalation path anywhere in the widget.
//     "No hallucinations" was already removed from this site once as unsupportable;
//     "never guesses" is the same claim reworded.
//   - wix-analytics shows "152 of 400 left" and "4 of 5 on your plan". Those are the Wix
//     listing's limits, not this site's: Basic here is 2,000 conversations and 10 sources
//     (src/config/plans.js). Correct on the marketplace, contradictory here.
// Both get re-exported and re-uploaded to the listing, then re-downloaded here, which
// fixes both surfaces at once. Until then the wording above is knowingly ahead of the
// product — check this note before quoting either card as a product claim.
export const APP_MARKET_SLIDES = [
    { key: "channels", label: "When they want a human, it's one tap", alt: "The widget home screen listing suggested questions above WhatsApp, email and phone contact options" },
    { key: "answers", label: "Answers from your own content", alt: "The chat view answering a question about insurance plans from the site owner's own material" },
    { key: "live", label: "Your website, answering customers 24/7", alt: "A live website with the PaperBrainz chat widget open in the corner" },
    { key: "knowledge", label: "Import your whole website in one click", alt: "The knowledge dashboard importing a website alongside an uploaded PDF and a Q&A pair" },
    { key: "appearance", label: "Match your brand, see it live as you edit", alt: "The appearance editor with colour and branding controls beside a live widget preview" },
    { key: "analytics", label: "Know what customers actually ask", alt: "The analytics overview showing conversation volume over six months and monthly usage" },
];


export const PLATFORM_PAGES = [
    {
        slug: "wix",
        // Wix is NOT a script-tag install. There is a real app in the Wix App Market that
        // installs itself with no code — do not describe it as pasting a <script> tag.
        // (An earlier version of this page did, because there is no Wix code in this repo.
        // That was a wrong inference: the app lives on Wix's side, not in this codebase.)
        installType: "app",
        appMarketUrl: "https://www.wix.com/app-market/paperbrainz-ai",
        demoUrl: "www.your-wix-site.com",
        path: "/ai-chatbot-for-wix",
        platform: "Wix",
        title: "AI Chatbot for Wix",
        heading: "Add an AI chatbot to your Wix site in one click",
        intro:
            "PaperBrainz is a Wix App Market app. Click Add to Site and it installs itself — no code, no embed element, nothing to paste. Then point it at your own pages and documents, and it answers your visitors around the clock.",
        metaDescription:
            "Add an AI customer support chatbot to your Wix site in one click from the Wix App Market. No code required. Train it on your own pages and PDFs and answer visitor questions 24/7.",
        problem: [
            "Wix site owners are usually the same person doing sales, delivery and support. Questions arrive at all hours through the contact form and sit there until someone opens the inbox.",
            "Most of those questions are already answered somewhere on the site — on a services page, in an FAQ, in a PDF price list. Visitors just did not find them.",
        ],
        install: [
            "Open PaperBrainz in the Wix App Market and click Add to Site. The app installs itself onto your site — there is no code to copy and nothing to paste.",
            "Add your content: upload PDFs or Word files, paste text or Q&A pairs, or let it read your existing Wix pages.",
            "Style the widget to match your site — colours, logo, position, greeting, light or dark.",
            "Publish. The chatbot is live on your Wix site and starts answering visitors straight away.",
        ],
        limitations: [
            "The chatbot answers from the content you give it. It does not read live Wix Stores data, so it cannot look up a specific order, stock level or booking.",
            "Pages behind a members-only login are not readable by the site scraper. Upload those documents directly instead.",
        ],
        faq: [
            {
                q: "Do I need to add any code?",
                a: "No. PaperBrainz is a Wix App Market app: you click Add to Site and it installs itself. There is no script tag, no HTML embed element and no developer needed.",
            },
            {
                q: "Where does the chatbot appear?",
                a: "It appears as a chat button on your published Wix site. You choose the corner it sits in, its colours, logo and greeting from the PaperBrainz dashboard.",
            },
            {
                q: "Can it read my existing Wix pages?",
                a: "Yes. Point it at your site and it reads your public page content as a knowledge source. You can also upload PDFs, DOC and DOCX files, paste text, or add question-and-answer pairs by hand.",
            },
            {
                q: "Will it slow my Wix site down?",
                a: "The widget loads asynchronously after your page content, so it does not block your pages from rendering.",
            },
            {
                q: "What happens if it does not know an answer?",
                a: "It tells the visitor it does not have that information and can point them to your contact details rather than guessing. You can edit that fallback message.",
            },
        ],
    },
    {
        slug: "wordpress",
        installType: "script",
        demoUrl: "www.your-wordpress-site.com",
        path: "/ai-chatbot-for-wordpress",
        platform: "WordPress",
        title: "AI Chatbot for WordPress",
        heading: "Add an AI chatbot to your WordPress site",
        intro:
            "If your WordPress site has a documentation section, a shop, or an FAQ page that nobody reads, PaperBrainz turns that content into a chatbot that answers visitors directly — without installing another heavy plugin.",
        metaDescription:
            "Add an AI customer support chatbot to WordPress. Train it on your posts, pages and PDFs, add one script tag — no plugin required. 14-day free trial, no credit card.",
        problem: [
            "WordPress sites accumulate content: posts, docs, product pages, a growing FAQ. The answer a visitor needs usually exists, three clicks away, on a page they will never open.",
            "The usual fix is another plugin. Every plugin is more weight, another update to babysit, another thing that can break your site.",
        ],
        install: [
            "Add your content — upload PDFs, paste text, or point PaperBrainz at your site URL to read your published pages.",
            "Customise the widget to match your theme.",
            "Copy the one-line script tag from the Integration page.",
            "Paste it into your theme's footer, or wherever your theme or a code-snippet plugin lets you add scripts before the closing body tag.",
        ],
        limitations: [
            "There is no PaperBrainz WordPress plugin. Installation is a script tag, which means no plugin updates to manage — but also no settings screen inside wp-admin.",
            "Content behind a login is not readable by the URL scraper. Upload those documents directly instead.",
        ],
        faq: [
            {
                q: "Is there a WordPress plugin?",
                a: "No. PaperBrainz installs as a single script tag, so there is nothing to keep updated in wp-admin. You add it through your theme's footer settings or any code-snippet plugin you already use.",
            },
            {
                q: "Will it work with my theme?",
                a: "Yes. The widget renders independently of your theme's styling, and you control its colours, position and shape from the PaperBrainz dashboard.",
            },
            {
                q: "Can it answer from my blog posts?",
                a: "Yes, if those posts are publicly reachable. Paste your site URL and PaperBrainz will scrape the public content.",
            },
            {
                q: "Does it work with WooCommerce?",
                a: "It works on any WordPress site including WooCommerce stores, answering from whatever content you add. It does not read live order or stock data.",
            },
        ],
    },
    {
        slug: "shopify",
        installType: "script",
        demoUrl: "your-store.myshopify.com",
        path: "/ai-chatbot-for-shopify",
        platform: "Shopify",
        title: "AI Chatbot for Shopify",
        heading: "Add an AI chatbot to your Shopify store",
        intro:
            "Pre-sales questions decide whether a visitor buys or leaves. Shipping times, returns, sizing, materials — if nobody answers within a minute, the visitor is gone. PaperBrainz answers from your own policies and product information, day or night.",
        metaDescription:
            "Add an AI customer support chatbot to your Shopify store. Train it on your policies, shipping info and product docs, add one script tag, and answer pre-sales questions 24/7.",
        problem: [
            "Most abandoned carts are not price problems. They are unanswered questions: will this arrive before the weekend, can I return it, does it fit.",
            "Your shipping and returns policies already answer those. They are just buried in the footer where nobody looks.",
        ],
        install: [
            "Add your content — upload or paste your shipping policy, returns policy, sizing guide and product information.",
            "Customise the widget to match your storefront.",
            "Copy the one-line script tag from the Integration page.",
            "Add it to your theme so it loads on every page, before the closing body tag. Shopify's theme editor documentation covers the current way to do this for your theme.",
        ],
        limitations: [
            "PaperBrainz does not connect to the Shopify Admin API. It cannot look up a specific customer's order status, live stock levels or tracking numbers.",
            "It answers from content you provide. Keep your shipping and returns text current and the answers stay current.",
        ],
        faq: [
            {
                q: "Can it tell customers where their order is?",
                a: "No. PaperBrainz does not connect to your Shopify order data, so it cannot look up individual orders or tracking numbers. It answers policy and product questions — shipping times, returns windows, sizing, materials.",
            },
            {
                q: "Can it recommend products?",
                a: "It can describe products from the information you add. It does not read live catalogue or stock data, so treat it as a knowledgeable assistant rather than a live inventory system.",
            },
            {
                q: "Will it work with my Shopify theme?",
                a: "Yes. It is a script tag, independent of your theme, and you control its appearance from the PaperBrainz dashboard.",
            },
            {
                q: "What happens when it does not know?",
                a: "It tells the visitor it does not have that information and can point them to your support contact. You can edit that fallback message.",
            },
        ],
    },
];

export const getPlatformPage = (slug) => PLATFORM_PAGES.find((p) => p.slug === slug) || null;

export default PLATFORM_PAGES;
